/**
 * effects module entry (owner: audio-fx-builder).
 * `createEffects(scene)` hands out ONE director per scene (a world scene and a cinematic host
 * created for the same scene share it, so weather layers are never doubled). `setEffectsFactory`
 * overrides construction (tests / a future replacement); `registerShowcase` adds `?showcase=effects`.
 */
import type Phaser from 'phaser';
import type { IEffectsDirector, IWorldScene } from '@/core/types/contracts';
import type { GameContext } from '@/core/context';
import { SHOWCASES } from '@/content/ShowcaseScene';
import { EffectsDirector, type EffectsOptions } from './EffectsDirector';
import { effectsShowcase } from './showcase';

export { EffectsDirector } from './EffectsDirector';
export type { EffectsOptions, EffectsState, EffectsLayerToggles } from './EffectsDirector';
export { ONE_SHOTS, ONE_SHOT_NAMES } from './oneshots';
export type { OneShot, OneShotFactory, FxHost } from './oneshots';
export { WEATHER_STATES, baseIntensity, blendIntensity, intensityFor, applyReduceMotion, rainSpawnPerStep } from './intensity';
export type { WeatherIntensity } from './intensity';
export { WeatherTrack } from './weatherTrack';
export { ensureFxTextures, FxTex } from './textures';
export { ShelterMask, exposedEaves, SHELTER_EDGE } from './shelter';
export type { Eave } from './shelter';
export * as fxGeometry from './geometry';

let factory: ((scene: IWorldScene) => IEffectsDirector) | null = null;
const perScene = new WeakMap<Phaser.Scene, IEffectsDirector>();

export function setEffectsFactory(f: ((scene: IWorldScene) => IEffectsDirector) | null): void { factory = f; }

/**
 * Director for `scene` — created on first request and attached immediately (attach is idempotent, so
 * the world scene's own `effects.attach(this)` is a no-op). Detached/forgotten on scene shutdown.
 */
export function createEffects(scene: IWorldScene, opts: EffectsOptions = {}): IEffectsDirector {
  const existing = perScene.get(scene);
  if (existing) return existing;
  let d: IEffectsDirector;
  if (factory) d = factory(scene);
  else {
    const ctx = scene.registry?.get('ctx') as GameContext | undefined;
    const real = new EffectsDirector({ seed: ctx?.seed, reduceMotion: ctx?.settings.reduceMotion, ...opts });
    real.attach(scene);
    d = real;
  }
  perScene.set(scene, d);
  scene.events?.once('shutdown', () => { if (perScene.get(scene) === d) perScene.delete(scene); });
  return d;
}

/** Director already created for `scene` (null before `createEffects`). */
export function effectsFor(scene: Phaser.Scene): IEffectsDirector | null { return perScene.get(scene) ?? null; }

/**
 * Registers `?showcase=effects` and the verification hook `window.__nantianFx.stateOf(sceneKey)` — the
 * live EffectsState (particles: drops / rainDrawn / rainMasked / drips…) of a running scene's director.
 */
export function registerShowcase(): void {
  SHOWCASES.effects = effectsShowcase;
  if (typeof window === 'undefined') return;
  (window as unknown as { __nantianFx?: unknown }).__nantianFx = {
    stateOf: (sceneKey: string) => {
      const game = (window as unknown as { __nantian?: { game?: Phaser.Game } }).__nantian?.game;
      const scene = game?.scene.getScene(sceneKey) ?? null;
      const d = scene ? perScene.get(scene) : null;
      return d && 'getState' in d ? (d as EffectsDirector).getState() : null;
    },
  };
}
